import JwtBaseClient from './JwtBaseClient';

/**
 * @class MyAcvServiceClient
 *
 * @description API wrapper for MyAcv Service API
 */
class MyAcvServiceClient extends JwtBaseClient {
  constructor() {
    super(
      `${import.meta.env.VITE_MY_ACV_BASE_URI}`,
      'response.data',
    );

    this.rootUri = 'api/v1';
  }

  /**
   * @function getSellerDashboardMetrics
   * @description Return an object with all the information about Seller Performance
   * for only one dealer.
   * @param {Number} dealerId
   * @returns {Promise}
   */
  getSellerDashboardMetrics(dealerId) {
    return this.get(`${this.rootUri}/seller_performance_metrics/${dealerId}`);
  }

  /**
   * @function getBillingDealerAccess
   * @description Return the dealer ids the current user is eligible to view Billing for
   * @returns {Promise}
   */
  getBillingDealerAccess() {
    return this.get(`${this.rootUri}/billing/dealer_access`);
  }

  /**
   * @function getPaymentsDashboardUrl
   * @description Return the signed Omni SSO URL for the Payments dashboard for one dealer
   * @param {Number} dealerId
   * @returns {Promise}
   */
  getPaymentsDashboardUrl(dealerId) {
    return this.get(`${this.rootUri}/billing/payments/omni_url/${dealerId}`);
  }

  /**
   * @function getSummaryBalance
   * @description Return an object with accumulated amount invoices balance for a dealership
   * @param {string} dealer_ids
   * @returns {Promise}
   */
  getSummaryBalance(params) {
    return this.get(`${this.rootUri}/invoice/summary`, params);
  }

  /**
   * @function getInvoices
   * @description Return a list of invoice objects based on query params
   * @param {Number} page
   * @param {Number} size
   * @param {String} invoice_status
   * @param {String} query
   * @param {String} dealer_ids
   * @returns {Promise}
   */
  getInvoices(params) {
    return this.get(`${this.rootUri}/invoice`, params);
  }

  /**
   * @function getExport
   * @description Return a csv file with invoices data
   * @param {String} from_date
   * @param {String} to_date
   * @param {String} dealer_ids
   * @returns {Promise}
   */
  getExport(params) {
    return this.get(`${this.rootUri}/invoice/export`, params, null);
  }

  /**
   * @function getDownloadInvoice
   * @description Download an Invoice PDF based on invoices ids param
   * @param {String} invoice_ids
   * @returns {Promise}
   */
  getDownloadInvoice(invoiceId) {
    const params = {
      invoice_ids: invoiceId,
    };
    return this.get(`${this.rootUri}/invoice/download`, params, null);
  }

  getTransportReleaseExport(params) {
    return this.get('internal/api/v1/myacv/auctions/selling/sold/export', params, null);
  }

  /**
   * @function markAsViewed
   * @description Mark won auction items as viewed by the buyer
   * @param {{ titleIds: Array, arbitrationIds: Array, transportationIds: Array }} params
   * @returns {Promise}
   */
  markAsViewed({ titleIds, arbitrationIds, transportationIds }) {
    return this.post(`${this.rootUri}/myacv/auctions/buying/won/view`, {
      title_ids: titleIds || null,
      arbitration_ids: arbitrationIds || null,
      transportation_ids: transportationIds || null,
    });
  }
}

export default MyAcvServiceClient;
